import { classNames } from '@bambi/component-library/src/utils/classNames';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Timeout } from 'react-number-format/types/types';

import { useOrganizationBillingStatusRetrieveQuery } from 'src/common/external/bambi-api/bambiApi';
import { useUserRoles } from 'src/features/auth/useUserRoles';

// Show the expanded banner for 10 seconds after 2 minutes.
const EXPAND_AFTER_TIMING = 1000 * 60 * 2;
const FLASH_TIMING = 1000 * 10;

export function DelinquentBanner() {
  const { isRideOrderingPortalUser } = useUserRoles();
  const { isLoading, data } = useOrganizationBillingStatusRetrieveQuery(
    undefined,
    {
      skip: isRideOrderingPortalUser,
    }
  );
  const [expand, setExpand] = useState(false);
  const expandTimerRef = useRef<Timeout>();
  const flashTimerRef = useRef<Timeout>();

  const handleExpand = useCallback(() => {
    setExpand(true);
    flashTimerRef.current = setTimeout(() => {
      setExpand(false);
    }, FLASH_TIMING);
  }, [setExpand]);

  useEffect(() => {
    expandTimerRef.current = setInterval(() => {
      handleExpand();
    }, EXPAND_AFTER_TIMING);

    return () => {
      clearInterval(expandTimerRef.current);
    };
  }, [handleExpand]);

  if (isLoading || !data?.is_delinquent) {
    return null;
  }

  return (
    <div
      data-testid="delinquent_banner"
      className={classNames(
        `sticky top-0 flex flex-col items-center justify-center bg-red-600 px-2 py-36 text-center text-lg font-bold text-white`,
        expand && 'h-[100vh] animate-pulse'
      )}
      style={{ zIndex: 20 }}
    >
      <span>
        Urgent: Payment is overdue. Immediate action is required to avoid
        service suspension.
      </span>
      {data.stripe_subscription_payment_link ? (
        <a
          href={data.stripe_subscription_payment_link}
          target="_blank"
          rel="noopener noreferrer"
          className="underline"
        >
          Click here to make a payment
        </a>
      ) : null}
    </div>
  );
}
